import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Header from "./Header";
import {
  fetchprofile,
  sendOTPForProfile,
  updateknownLanguage,
  updatepassword,
  updateprofile,
  verifyOTPForProfile,
} from "@/store/thunks/studentLogin";
import { useDispatch, useSelector } from "react-redux";
import { BASE_URL } from "@/utils/constants";

import StudentSelectInput from "@/components/shared/StudentSelectInput";
import OrganizationSelectInput from "@/components/shared/OrganizationSelectInput";
import CollegeSelectInput from "@/components/shared/CollegeSelectInput";
import { Formik, Form } from "formik";
import apiClient from "@/services/api/apiClientStudent";
import JSEncrypt from "jsencrypt";
import { Eye, EyeOff } from "lucide-react";

const checkPasswordRules = (value = "") => ({
  minLength: value.length >= 8,
  hasUppercase: /[A-Z]/.test(value),
  hasLowercase: /[a-z]/.test(value),
  hasNumber: /[0-9]/.test(value),
  hasSpecialChar: /[@#$%^&+=]/.test(value),
});

const Rule = ({ ok, text }) => (
  <div className={`flex items-center gap-2 ${ok ? "text-green-600" : "text-gray-400"}`}>
    <span>{ok ? "✔" : "✖"}</span>
    <span>{text}</span>
  </div>
);

const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const MyProfile = () => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // UI data (shown to the user)
  const [userData, setUserData] = useState(null);
  const [editedData, setEditedData] = useState(null);
  // Raw IDs coming from the API (used for the payload)
  const [rawData, setRawData] = useState(null);

  // Master data
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [publicKey, setPublicKey] = useState("");
  const [colleges, setColleges] = useState([]);
  const [knownLanguages, setKnownLanguages] = useState([]);
  const [allLanguages, setAllLanguages] = useState([]);

  const [contactModal, setContactModal] = useState({
    open: false,
    type: null, // "email" | "mobile"
    value: "",
    otp: "",
    otpSent: false,
  });


  const { userId } = useSelector((state) => state.studentAuth);

  // Selected IDs – exactly the same pattern as Registration
  const [chosen, setChosen] = useState({ state: null, org: null });

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [isFocused, setIsFocused] = useState(false);

  const passwordRules = checkPasswordRules(passwordData.newPassword);

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);




  // -------------------------------------------------
  // 1. FETCH MASTER DATA (identical to Registration)
  // -------------------------------------------------
  const getStates = async () => {
    const r = await apiClient(`${BASE_URL}api/user/register/states`);
    setStates(r.data || []);
  };

  const getCities = useCallback(
    async (stateId, keyword = "") => {
      if (!stateId) return;
      const r = await apiClient(
        `${BASE_URL}api/user/register/cities?stateId=${stateId}&limit=100&searchKeyword=${keyword}`
      );
      setCities(r.data || []);
    },
    []
  );

  const getOrgs = useCallback(
    async (stateId, keyword = "") => {
      if (!stateId) return;
      const r = await apiClient(
        `${BASE_URL}api/user/register/organizations?stateId=${stateId}&limit=100&searchKeyword=${keyword}`
      );
      setOrgs(r.data || []);
    },
    []
  );

  const getColleges = useCallback(
    async (orgId, keyword = "") => {
      if (!orgId) return;
      const r = await apiClient(
        `${BASE_URL}api/user/register/colleges?organizationId=${orgId}&limit=100&searchKeyword=${keyword}`
      );
      setColleges(r.data || []);
    },
    []
  );

  const getLanguages = async () => {
    const r = await apiClient(`${BASE_URL}api/user/profile/languages?userId=${userId}`);
    setKnownLanguages(r.data || []);
  };

  const getAllLanguages = async () => {
    const r = await apiClient(`${BASE_URL}api/user/register/languages`);
    setAllLanguages(r.data || []);
  };

  const addLanguage = (languageId) => {
    const lang = allLanguages.find(l => l.languageId === Number(languageId));
    if (!lang) return;

    setKnownLanguages(prev => {
      // prevent duplicates
      if (prev.some(l => l.languageId === lang.languageId)) return prev;
      return [...prev, lang];
    });
  };

  const removeLanguage = (languageId) => {
    setKnownLanguages(prev =>
      prev.filter(l => l.languageId !== languageId)
    );
  };


  // -------------------------------------------------
  // 2. LOAD PROFILE + PRE-POPULATE DEPENDENT DROPDOWNS
  // -------------------------------------------------
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        getAllLanguages();
        const res = await dispatch(fetchprofile());
        if (!res?.data) throw new Error("No profile data");

        const d = res.data;

        const display = {
          fname: d.firstName || "",
          mname: d.middleName || "",
          lname: d.lastName || "",
          username: d.username || "",
          email: d.email || "",
          gender:
            d.gender === "M" ? "Male" : d.gender === "F" ? "Female" : "Others",
          mobileNumber: d.mobileNumber || "",
          enrollmentNumber: d.enrollmentNumber || "",
          state: d.stateName || "",
          city: d.cityId ? { id: d.cityId, name: d.cityName || "" } : null,
          university: d.organizationId ? { id: d.organizationId, name: d.organizationName || "" } : null,
          college: d.collegeId ? { id: d.collegeId, name: d.collegeName || "" } : null,
          knownLanguages: allLanguages
        };


        setUserData(display);
        setEditedData(display);
        // 1️⃣ RAW DATA → IDs only (for submit)
        setRawData({
          stateId: d.stateId,
          cityId: d.cityId,
          organizationId: d.organizationId,
          collegeId: d.collegeId,
        });

        // ---- load master data ----
        await getStates();
        getLanguages();

        // ---- pre-load dependent data (same IDs as in registration) ----
        if (d.stateId) {
          setChosen((c) => ({ ...c, state: d.stateId }));
          await getCities(d.stateId);
          await getOrgs(d.stateId);
        }
        if (d.organizationId) {
          setChosen((c) => ({ ...c, org: d.organizationId }));
          await getColleges(d.organizationId);
        }
      } catch (e) {
        setError(e.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };
    load();
    fetchKey();
  }, [dispatch, getCities, getOrgs, getColleges]);

  useEffect(() => {
    if (contactModal.open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [contactModal.open]);


  // -------------------------------------------------
  // 3. HANDLERS – keep IDs in `rawData` & UI in `editedData`
  // -------------------------------------------------
  const onStateChange = (stateName) => {
    const state = states.find((s) => s.stateName === stateName);
    setEditedData((e) => ({
      ...e,
      state: stateName,
      city: "",
      university: "",
      college: "",
    }));
    if (state) {
      setChosen((c) => ({ ...c, state: state.stateId }));
      setRawData((r) => ({ ...r, stateId: state.stateId }));
      getCities(state.stateId);
      getOrgs(state.stateId);
      setCities([]); setOrgs([]); setColleges([]);
    }
  };

  const onCityChange = (cityId) => {
    const city = cities.find(c => c.cityId === cityId);
    if (!city) return;

    setRawData((r) => ({ ...r, cityId }));
    setEditedData((e) => ({ ...e, city: city.cityName }));
  };



  const onOrgChange = (orgName) => {
    const org = orgs.find((o) => o.organizationName === orgName);
    setEditedData((e) => ({ ...e, university: orgName, college: "" }));
    if (org) {
      setChosen((c) => ({ ...c, org: org.organizationId }));
      setRawData((r) => ({ ...r, organizationId: org.organizationId }));  // ← this line MUST exist
      getColleges(org.organizationId);
      setColleges([]);
    }
  };

  const onCollegeChange = (collegeName) => {
    const college = colleges.find((c) => c.collegeName === collegeName);
    setEditedData((e) => ({ ...e, college: collegeName }));
    if (college) setRawData((r) => ({ ...r, collegeId: college.collegeId }));  // ← must have this
  };

  // ===== Password ===== 
  const handlePasswordChange = (field, value) => {
    setPasswordData({ ...passwordData, [field]: value });
  };

  // Fetch public key from API
  const fetchKey = async () => {
    try {
      const response = await apiClient(`${BASE_URL}api/user/public-key`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.data) {
        setPublicKey(response.data);
      } else {
        console.error("Failed to fetch public key");
      }
    } catch (err) {
      console.error("[fetchKey] Error:", err);
    }
  };


  const handleSavePassword = async () => {
    if (!passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.warning("Please fill in all password fields!");
      return;
    } if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match!");
      return;
    }
    if (!publicKey) {
      toast.error("Unable to encrypt password — public key not loaded yet!");
      return;
    }
    try {
      // 🔐 Encrypt passwords
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(publicKey);

      const encryptedData = {
        oldPassword: encrypt.encrypt(passwordData.oldPassword),
        password: encrypt.encrypt(passwordData.confirmPassword),
      };


      // 🚀 Dispatch to backend
      const res = await dispatch(updatepassword(encryptedData));
      if (res.success) {
        toast.success("Password changed successfully!");
      }
      else {
        toast.error(res.message || "Failed to change password!");
      }

      // 🧹 Reset state
      setIsChangingPassword(false);
      setPasswordData({ oldPassword: "", newPassword: "", confirmPassword: "" });

    } catch (err) {
      console.error("Error encrypting password:", err);
      toast.error("Something went wrong while encrypting password!");
    }
  };

  // -------------------------------------------------
  // 4. SAVE
  // -------------------------------------------------
  const handleSave = async () => {

    console.log("Edited data on save:", editedData);
    const payload = {
      firstName: editedData.fname.trim(),
      middleName: editedData.mname.trim(),
      lastName: editedData.lname.trim(),
      gender:
        editedData.gender === "Male"
          ? "M"
          : editedData.gender === "Female"
            ? "F"
            : "O",
      enrollmentNumber: editedData.enrollmentNumber,
      stateId: rawData.stateId,
      cityId: rawData.cityId,
      organizationId: rawData.organizationId,
      collegeId: rawData.collegeId,
    };

    const res = await dispatch(updateprofile(payload));
    const langPayload = {
      knowLangIds: knownLanguages.map(l => l.languageId),
    };
    const resLang = await dispatch(updateknownLanguage(langPayload));
    if (res.success && resLang.success) {
      setUserData(editedData);
      setIsEditing(false);
      toast.success("Profile updated!");
    }
    else {
      console.error(res);
      console.error(resLang);
      toast.error("Failed to update profile!");
    }
  };

  const sendEmailOtp = async (email) => {
    if (!email) {
      toast.warning("Enter email first");
      return;
    }

    if (!emailRegex.test(email)) {
    toast.error("Please enter a valid email address!");
    return;
  }

    try {
      const payload = { email };
      const data = await dispatch(sendOTPForProfile("email", payload));
      if (data.success || data.status === 'success') {
        toast.success(data.message || "OTP sent to email!");
        setContactModal(m => ({ ...m, otpSent: true }));
      } else {
        toast.error(data.message || "Failed to send OTP to email");
      }
    } catch (error) {
      console.error("Failed to send EMAIL OTP:", error);
      toast.error("Error sending OTP to email.");
    }
  };


  const sendMobileOtp = async (mobile) => {
    if (!mobile) {
      toast.warning("Enter email first");
      return;
    }
    try {
      const payload = { mobile };
      const data = await dispatch(sendOTPForProfile("mobile", payload));
      if (data.success || data.status === 'success') {
        toast.success(data.message || "OTP sent to mobile!");
        setContactModal(m => ({ ...m, otpSent: true }));
      } else {
        toast.error(data.message || "Failed to send OTP to mobile");
      }
    } catch (error) {
      console.error("Failed to send MOBILE OTP:", error);
      toast.error("Error sending OTP to mobile.");
    }
  };

  const verifyOtp = async () => {
    setUserData((u) => ({
      ...u,
      [contactModal.type === "email" ? "email" : "mobileNumber"]:
        contactModal.value,
    }));
    try {
      const payload = {
        otp: contactModal.otp,
        [contactModal.type === "email" ? "email" : "mobile"]: contactModal.value,
      }
      const data = await dispatch(verifyOTPForProfile(contactModal.type, payload));
      if (data.success || data.status === 'success') {
        toast.success(data.message || "Contact info updated!");
      } else {
        toast.error(data.message || "Failed to update contact info");
      }
    } catch (error) {
      console.error("Failed to verify OTP:", error);
      toast.error("Error verifying OTP.");
    } finally {
      setContactModal({
        open: false,
        type: null,
        value: "",
        otp: "",
        otpSent: false,
      });

    }

  };

  // -------------------------------------------------
  // 5. RENDER
  // -------------------------------------------------
  if (loading)
    return (
      <div>
        <Header />
        <div className="flex justify-center items-center min-h-[70vh]">
          <div className="w-12 h-12 border-4 border-orange-300 border-t-orange-600 rounded-full animate-spin"></div>
        </div>
      </div>
    );

  if (error) return <div><Header />{error}</div>;

  return (
    <div className={contactModal.open ? "pointer-events-none select-none" : ""}>
      <Header />
      <div className=" pt-2 pb-4 flex justify-center items-start px-2">
        <div className="bg-white shadow-xl rounded-3xl p-8 w-full max-w-4xl border border-orange-200">

          <h1 className="text-3xl font-bold text-[#ec7026] text-center mb-8">
            {isChangingPassword ? "Change Password" : "My Profile"}
          </h1>

          {isChangingPassword ? (
            // ===================== CHANGE PASSWORD VIEW =====================
            <div className="w-full max-w-md mx-auto mt-6 bg-gray-50 p-6 rounded-xl shadow-inner border border-gray-200 relative">
              <div className="space-y-4">

                {/* OLD PASSWORD */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-600">
                    Old Password
                  </label>

                  <input
                    type={showOldPassword ? "text" : "password"}
                    value={passwordData.oldPassword}
                    onChange={(e) => handlePasswordChange("oldPassword", e.target.value)}
                    className="mt-1 w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ec7026]"
                  />

                  <button
                    type="button"
                    onClick={() => setShowOldPassword(p => !p)}
                    className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700"
                  >
                    {showOldPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>


                {/* NEW PASSWORD (WITH TOOLTIP) */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-600">
                    New Password
                  </label>

                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange("newPassword", e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    className="mt-1 w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ec7026]"
                  />

                  <button
                    type="button"
                    onClick={() => setShowNewPassword(p => !p)}
                    className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>

                  {/* PASSWORD RULE TOOLTIP */}
                  {isFocused && passwordData.newPassword.length > 0 && (
                    <div className="absolute z-50 top-full mt-2 w-[300px] p-3 bg-white border rounded-lg shadow-xl text-xs space-y-1">
                      <Rule ok={passwordRules.minLength} text="At least 8 characters" />
                      <Rule ok={passwordRules.hasUppercase} text="One uppercase letter (A–Z)" />
                      <Rule ok={passwordRules.hasLowercase} text="One lowercase letter (a–z)" />
                      <Rule ok={passwordRules.hasNumber} text="One number (0–9)" />
                      <Rule ok={passwordRules.hasSpecialChar} text="One special character (@#$%^&+=)" />
                    </div>
                  )}
                </div>


                {/* CONFIRM PASSWORD */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-600">
                    Confirm New Password
                  </label>

                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange("confirmPassword", e.target.value)}
                    className="mt-1 w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ec7026]"
                  />

                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(p => !p)}
                    className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>


              </div>

              {/* Buttons */}
              <div className="flex justify-center gap-4 mt-8">
                <button
                  onClick={handleSavePassword}
                  className="px-6 py-2 bg-[#ec7026] text-white rounded-lg font-semibold hover:bg-[#d45f17] transition-all duration-200"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setIsChangingPassword(false)}
                  className="px-6 py-2 bg-gray-300 text-gray-800 rounded-lg font-semibold hover:bg-gray-400 transition-all duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>

          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* ---------- Simple fields ---------- */}
                {[
                  { label: "First Name", key: "fname", editable: true },
                  { label: "Middle Name", key: "mname", editable: true },
                  { label: "Last Name", key: "lname", editable: true },
                  { label: "Username", key: "username" },
                  { label: "Email", key: "email", editable: true },
                  {
                    label: "Gender",
                    key: "gender",
                    type: "select",
                    options: ["Male", "Female", "Others"],
                  },
                  { label: "Mobile Number", key: "mobileNumber", editable: true },
                  { label: "Enrollment No.", key: "enrollmentNumber", editable: true },
                ].map(({ label, key, editable, type, options }) => {

                  // ✅ EMAIL & MOBILE (READ-ONLY + EDIT BUTTON)
                  if (key === "email" || key === "mobileNumber") {
                    return (
                      <div key={key}>
                        <label className="block text-sm font-medium text-gray-600 uppercase">
                          {label}
                        </label>

                        <div className="mt-1 flex items-center justify-between gap-3">
                          <p className="text-lg font-semibold text-gray-800">
                            {userData[key] || "-"}
                          </p>

                          {isEditing && (
                            <button
                              type="button"
                              onClick={() =>
                                setContactModal({
                                  open: true,
                                  type: key === "email" ? "email" : "mobile",
                                  value: userData[key],
                                  otp: "",
                                })
                              }
                              className="px-4 py-1 text-sm bg-[#ec7026] text-white rounded-md"
                            >
                              Change
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // ✅ ALL OTHER FIELDS (UNCHANGED)
                  return (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-600 uppercase">
                        {label}
                      </label>

                      {isEditing && editable ? (
                        type === "select" ? (
                          <select
                            value={editedData[key] || ""}
                            onChange={(e) =>
                              setEditedData((d) => ({ ...d, [key]: e.target.value }))
                            }
                            className="mt-1 w-full px-4 py-2 border rounded-lg"
                          >
                            <option value="">Select</option>
                            {options?.map((o) => (
                              <option key={o} value={o}>{o}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={editedData[key] || ""}
                            onChange={(e) =>
                              setEditedData((d) => ({ ...d, [key]: e.target.value }))
                            }
                            className="mt-1 w-full px-4 py-2 border rounded-lg"
                          />
                        )
                      ) : (
                        <p className="mt-1 text-lg font-semibold text-gray-800">
                          {userData[key] || "-"}
                        </p>
                      )}
                    </div>
                  );
                })}


                {/* ---------- STATE (custom component) ---------- */}
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-600 uppercase">
                    State
                  </label>

                  {isEditing ? (
                    <select
                      value={editedData.state}
                      onChange={(e) => onStateChange(e.target.value)}
                      className="mt-1 w-full px-4 py-2 border rounded-lg"
                    >
                      <option value="">Select State</option>
                      {states.map((s) => (
                        <option key={s.stateId} value={s.stateName}>
                          {s.stateName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="mt-1 text-lg font-semibold text-gray-800">
                      {userData.state || "-"}
                    </p>
                  )}
                </div>



                {/* ---------- CITY (StudentSelectInput) ---------- */}
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-600 uppercase mb-1">
                    City
                  </label>

                  {isEditing ? (
                    <Formik
                      initialValues={{ city: editedData.city || "" }}
                      enableReinitialize
                    >
                      {({ setFieldValue, values }) => (
                        <Form>
                          <StudentSelectInput
                            name="city"
                            data={cities}
                            // value={editedData.city} // <-- now matches ID
                            onChange={(selected) => {
                              setFieldValue("city", {
                                id: selected.cityId,
                                name: selected.cityName
                              });
                              onCityChange(selected.cityId);
                            }}
                            onSearch={(kw) => chosen.state && getCities(chosen.state, kw)}
                            label={editedData.city}
                            showLabel={false}
                          >
                            <option value="">Select City</option>
                            {cities.map(c => (
                              <option key={c.cityId} value={c.cityId}>
                                {c.cityName}   {/* Display name, but value is ID */}
                              </option>
                            ))}
                          </StudentSelectInput>
                        </Form>
                      )}
                    </Formik>

                  ) : (
                    <p className="mt-1 text-lg font-semibold text-gray-800">
                      {userData.city?.name || "-"}
                    </p>
                  )}
                </div>



                {/* ---------- UNIVERSITY (Organisation) ---------- */}
                <div>
                  {isEditing ? (
                    <Formik
                      initialValues={{
                        university: editedData.university
                      }}
                      enableReinitialize
                    >
                      {({ setFieldValue, values }) => (
                        <Form>
                          <OrganizationSelectInput
                            label="Organisation"
                            name="university"
                            data={orgs}
                            // Remove value prop – not needed
                            // value={values.university}
                            onChange={(selected) => {
                              setFieldValue("university", {
                                id: selected.organizationId,
                                name: selected.organizationName
                              });
                              onOrgChange(selected.organizationName);
                            }}
                            onSearch={(kw) => chosen.state && getOrgs(chosen.state, kw)}
                          >
                            <option value="">Select Organisation</option>
                            {orgs.map(o => (
                              <option key={o.organizationId} value={o.organizationId}>
                                {o.organizationName}
                              </option>
                            ))}
                          </OrganizationSelectInput>
                        </Form>
                      )}
                    </Formik>
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-gray-600 uppercase">
                        Organisation
                      </label>
                      <p className="mt-1 text-lg font-semibold text-gray-800">
                        {userData.university.name || "-"}
                      </p>
                    </>
                  )}
                </div>

                {/* ---------- COLLEGE ---------- */}
                <div>
                  {isEditing ? (
                    <Formik initialValues={{ college: editedData.college }} enableReinitialize>
                      {({ setFieldValue, values }) => (
                        <Form>
                          <CollegeSelectInput
                            label="College"
                            name="college"
                            data={colleges}
                            // value={values.college}
                            onChange={(selected) => {
                              setFieldValue("college", {
                                id: selected.collegeId,
                                name: selected.collegeName
                              });
                              onCollegeChange(selected.collegeName);
                            }}
                            onSearch={(kw) => chosen.org && getColleges(chosen.org, kw)}
                          >
                            <option value="">Select College</option>
                            {colleges.map(c => (
                              <option key={c.collegeId} value={c.collegeName}>
                                {c.collegeName}
                              </option>
                            ))}
                          </CollegeSelectInput>
                        </Form>
                      )}
                    </Formik>
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-gray-600 uppercase">
                        College
                      </label>
                      <p className="mt-1 text-lg font-semibold text-gray-800">
                        {userData.college.name || "-"}
                      </p>
                    </>
                  )}
                </div>

              </div>
              {/* ---------- known languages ---------- */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-600 uppercase">
                  Known Languages
                </label>

                {isEditing ? (
                  <>
                    {/* Selected languages (chips) */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {knownLanguages.map(lang => (
                        <span
                          key={lang.languageId}
                          className="flex items-center gap-2 bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium"
                        >
                          {lang.languageName}
                          <button
                            onClick={() => removeLanguage(lang.languageId)}
                            className="text-orange-600 hover:text-red-600 font-bold "
                            title="Remove"
                          >
                            ✕
                          </button>
                        </span>
                      ))}

                      {knownLanguages.length === 0 && (
                        <span className="text-sm text-gray-400 italic">
                          No languages selected
                        </span>
                      )}
                    </div>

                    {/* Add new language */}
                    <select
                      className="mt-3 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#ec7026]"
                      onChange={(e) => {
                        addLanguage(e.target.value);
                        e.target.value = "";
                      }}
                      defaultValue=""
                    >
                      <option value="">Add a language</option>

                      {allLanguages
                        .filter(
                          lang =>
                            !knownLanguages.some(
                              k => k.languageId === lang.languageId
                            )
                        )
                        .map(lang => (
                          <option key={lang.languageId} value={lang.languageId}>
                            {lang.languageName}
                          </option>
                        ))}
                    </select>
                  </>
                ) : (
                  <p className="mt-1 text-lg font-semibold text-gray-800">
                    {knownLanguages.length
                      ? knownLanguages.map(l => l.languageName).join(", ")
                      : "-"}
                  </p>
                )}
              </div>


              {/* ---------- ACTION BUTTONS ---------- */}
              <div className="flex justify-center gap-4 mt-10">
                {isEditing ? (
                  <>
                    <button
                      onClick={handleSave}
                      className="px-6 py-3 bg-[#ec7026] text-white rounded-lg font-bold hover:bg-[#d45f17]"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-6 py-3 bg-gray-300 rounded-lg font-bold"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="px-8 py-3 bg-[#ec7026] text-white rounded-lg font-bold hover:bg-[#d45f17]"
                    >
                      Edit Profile
                    </button>
                    <button onClick={() => setIsChangingPassword(true)} className="px-6 py-2 bg-gray-500 text-white rounded-lg font-semibold shadow-md hover:bg-gray-600 transition-all duration-200" > 🔒 Change Password </button>
                  </>
                )}

                {contactModal.open && (
                  <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                    aria-modal="true"
                    role="dialog"
                  >
                    <div
                      className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl pointer-events-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h2 className="text-xl font-bold text-[#ec7026] mb-4">
                        Update {contactModal.type === "email" ? "Email" : "Mobile Number"}
                      </h2>

                      <div className="flex gap-3 mb-4">
                        {/* INPUT */}
                        <input
                          type={contactModal.type === "email" ? "email" : "text"}
                          value={contactModal.value}
                          disabled={contactModal.otpSent}
                          onChange={(e) =>
                            setContactModal((m) => ({ ...m, value: e.target.value }))
                          }
                          placeholder={`Enter new ${contactModal.type}`}
                          className="w-[300px] px-4 py-2 border rounded-lg mb-3"
                        />

                        {/* SEND OTP */}
                        <button
                          onClick={() =>
                            contactModal.type === "email"
                              ? sendEmailOtp(contactModal.value)
                              : sendMobileOtp(contactModal.value)
                          }
                          className={`py-2 px-2 text-white rounded-lg mb-3
                              ${contactModal.otpSent
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-[#ec7026] hover:bg-[#d45f17]"}`}
                        >
                          Send OTP
                        </button>
                      </div>

                      {/* OTP INPUT */}
                      <input
                        type="text"
                        value={contactModal.otp}
                        disabled={!contactModal.otpSent}
                        onChange={(e) => {
                          let value = e.target.value;

                          if (contactModal.type === "mobile") {
                            // allow only digits and max 6 characters
                            value = value.replace(/\D/g, "").slice(0, 6);
                          }

                          setContactModal((m) => ({ ...m, otp: value }));
                        }}
                        placeholder="Enter OTP"
                        className="w-full px-4 py-2 border rounded-lg mb-4"
                      />


                      {/* ACTIONS */}
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => setContactModal({
                            open: false,
                            type: null,
                            value: "",
                            otp: "",
                            otpSent: false,
                          })}
                          className="px-4 py-2 bg-gray-300 rounded-lg"
                        >
                          Cancel
                        </button>

                        <button
                          onClick={verifyOtp}
                          className="px-4 py-2 bg-[#ec7026] text-white rounded-lg"
                        >
                          Verify & Update
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>)}
        </div>
      </div>
    </div>
  );
};

export default MyProfile;